import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from './config';
import './AdminModeration.css';

const AdminModeration = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('bugs');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [betaData, setBetaData] = useState({ pending_requests: [], approved_users: [] });
  const [bugReports, setBugReports] = useState([]);
  const [bugStatusFilter, setBugStatusFilter] = useState('pending');

  const fetchAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [betaRes, bugRes] = await Promise.all([
        apiClient.get('/api/admin/beta/requests'),
        apiClient.get(`/api/admin/feedback/bug-reports?status=${encodeURIComponent(bugStatusFilter)}&limit=200`)
      ]);
      setBetaData(betaRes.data || { pending_requests: [], approved_users: [] });
      setBugReports((bugRes.data && bugRes.data.reports) || []);
    } catch (err) {
      const message = err?.response?.data?.error || 'Failed to load admin moderation data.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bugStatusFilter]);

  const approveBeta = async (userId) => {
    try {
      await apiClient.post(`/api/admin/beta/approve/${userId}`);
      await fetchAll();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to approve beta request.');
    }
  };

  const rejectBeta = async (userId) => {
    const reason = window.prompt('Enter rejection reason:', 'Your request does not meet our current beta criteria.');
    if (reason === null) return;
    try {
      await apiClient.post(`/api/admin/beta/reject/${userId}`, { reason });
      await fetchAll();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to reject beta request.');
    }
  };

  const approveBug = async (report) => {
    const severity = window.prompt('Severity to approve with (low|medium|high|critical):', report.severity || 'medium');
    if (severity === null) return;
    const adminNotes = window.prompt('Optional admin notes:', report.admin_notes || '');
    if (adminNotes === null) return;
    try {
      await apiClient.post(`/api/admin/feedback/bug-reports/${report.id}/approve`, {
        severity,
        admin_notes: adminNotes
      });
      await fetchAll();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to approve bug report.');
    }
  };

  const rejectBug = async (report) => {
    const rejectionReason = window.prompt('Rejection reason:', report.rejection_reason || '');
    if (rejectionReason === null) return;
    if (!rejectionReason.trim()) {
      alert('Rejection reason is required.');
      return;
    }
    const adminNotes = window.prompt('Optional admin notes:', report.admin_notes || '');
    if (adminNotes === null) return;
    try {
      await apiClient.post(`/api/admin/feedback/bug-reports/${report.id}/reject`, {
        rejection_reason: rejectionReason,
        admin_notes: adminNotes
      });
      await fetchAll();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to reject bug report.');
    }
  };

  return (
    <div className="admin-moderation-container">
      <div className="admin-moderation-header">
        <button className="admin-back-btn" onClick={() => navigate('/')}>← Back to Home</button>
        <h1>Admin Moderation</h1>
        <p>Review beta requests and bug bounty submissions in one place.</p>
      </div>

      <div className="admin-tabs">
        <button
          className={activeTab === 'bugs' ? 'active' : ''}
          onClick={() => setActiveTab('bugs')}
        >
          Bug Reports ({bugReports.length})
        </button>
        <button
          className={activeTab === 'beta' ? 'active' : ''}
          onClick={() => setActiveTab('beta')}
        >
          Beta Requests ({(betaData.pending_requests || []).length})
        </button>
        <button onClick={fetchAll}>Refresh</button>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {loading && <div className="admin-loading">Loading moderation data...</div>}

      {!loading && activeTab === 'bugs' && (
        <div>
          <div className="admin-filters">
            <label>
              Status:
              <select value={bugStatusFilter} onChange={(e) => setBugStatusFilter(e.target.value)}>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="all">All</option>
              </select>
            </label>
          </div>
          <div className="admin-list">
            {bugReports.length === 0 && <div className="admin-empty">No bug reports found.</div>}
            {bugReports.map((report) => (
              <div key={report.id} className="admin-card">
                <h3>#{report.id} - {report.title}</h3>
                <p><strong>User:</strong> {report.user_email}</p>
                <p><strong>Status:</strong> {report.status} | <strong>Severity:</strong> {report.severity}</p>
                <p><strong>Summary:</strong> {report.summary}</p>
                <p><strong>Steps:</strong> {report.steps_to_reproduce}</p>
                <p><strong>Expected:</strong> {report.expected_behavior}</p>
                <p><strong>Actual:</strong> {report.actual_behavior}</p>
                <p><strong>Environment:</strong> {report.environment}</p>
                {report.suggested_fix ? <p><strong>Suggested Fix:</strong> {report.suggested_fix}</p> : null}
                {report.attachments_or_logs ? <p><strong>Logs/Attachments:</strong> {report.attachments_or_logs}</p> : null}
                {report.status === 'pending' && (
                  <div className="admin-actions">
                    <button className="approve" onClick={() => approveBug(report)}>Approve</button>
                    <button className="reject" onClick={() => rejectBug(report)}>Reject</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && activeTab === 'beta' && (
        <div className="admin-list">
          {(betaData.pending_requests || []).length === 0 && (
            <div className="admin-empty">No pending beta requests.</div>
          )}
          {(betaData.pending_requests || []).map((req) => (
            <div key={req.id} className="admin-card">
              <h3>{req.first_name} {req.last_name}</h3>
              <p><strong>Email:</strong> {req.email}</p>
              <p><strong>Reason:</strong> {req.reason}</p>
              <div className="admin-actions">
                <button className="approve" onClick={() => approveBeta(req.id)}>Approve</button>
                <button className="reject" onClick={() => rejectBeta(req.id)}>Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminModeration;
